import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Theme from 'core/themes/model'

type DropdownMenuProps = {
  isOpen: boolean
  width: number
  compact?: boolean
  fullWidth?: boolean
  children: ReactNode
  ref?: React.Ref<HTMLUListElement>
}

const useStyles = makeStyles<Theme>((theme) => ({
  dropdownMenu: {
    opacity: 0,
    maxHeight: '1rem',
    visibility: 'hidden',
    pointerEvents: 'none',
    transition: 'opacity .2s ease, max-height .2s ease',
    zIndex: 1300, // Higher than modal z-index
    color: theme.components.dropdown.color,
    backgroundColor: theme.components.dropdown.background,
    right: -1,
    padding: 0,
    marginTop: 0,
    position: 'fixed', // Changed to fixed for portal positioning
    width: '100%',
    minWidth: 150,
    maxWidth: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    outline: '0',
    borderRadius: 4,
    borderColor: theme.components.dropdown.border,
    borderWidth: 1,
    borderStyle: 'solid',
    border: 'none',
  },
  dropdownMenuOpen: {
    opacity: 1,
    maxHeight: '20rem',
    visibility: 'visible',
    pointerEvents: 'auto',
    border: null,
  },
  dropdownMenuCompact: {
    width: null,
    maxWidth: 300,
  },
  dropdownMenuFullWidth: {
    width: null,
    maxWidth: null,
  },
}))

export default React.forwardRef<HTMLUListElement, DropdownMenuProps>((props, ref) => {
  const { isOpen, children, compact, fullWidth, ...rest } = props
  const classes = useStyles()
  const portalTargetRef = useRef<HTMLElement | null>(document.createElement('div'))
  const [position, setPosition] = useState<{
    top?: number
    bottom?: number
    left: number
    width: number
  }>({ left: 0, width: 0 })
  const placeholderRef = useRef<HTMLDivElement>(null)

  // Create portal target on mount
  useEffect(() => {
    document.body.appendChild(portalTargetRef?.current)
    return () => {
      document.body.removeChild(portalTargetRef?.current)
    }
  }, [])

  // Update position when menu opens and on scroll/resize
  useLayoutEffect(() => {
    if (!isOpen || !placeholderRef.current) return () => {}

    const updatePosition = () => {
      if (placeholderRef.current) {
        // Get the parent inputFrame element for accurate positioning
        const inputFrame = placeholderRef.current.closest('.inputFrame') as HTMLElement
        if (!inputFrame) return

        const rect = inputFrame.getBoundingClientRect()
        const maxHeight = 320 // 20rem converted to pixels
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top

        // If not enough space below, show above if there's more space
        const showAbove = spaceBelow < maxHeight && spaceAbove > spaceBelow

        if (showAbove) {
          // Position dropdown above: dropdown's bottom touches input's top
          const DROPDOWN_MARGIN = 16 // Space between dropdown and input when positioned above
          setPosition({
            bottom: window.innerHeight - rect.top - DROPDOWN_MARGIN,
            left: rect.left,
            width: rect.width,
          })
        } else {
          // Position dropdown below: dropdown's top touches input's bottom
          setPosition({
            top: rect.bottom,
            bottom: undefined,
            left: rect.left,
            width: rect.width,
          })
        }
      }
    }

    updatePosition()

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen])

  const menuContent = (
    <ul
      className={clsx(classes.dropdownMenu, 'dropdownMenu', {
        [classes.dropdownMenuOpen]: isOpen,
        [classes.dropdownMenuCompact]: compact,
        [classes.dropdownMenuFullWidth]: fullWidth,
      })}
      {...rest}
      ref={ref}
      style={{
        top: position.top,
        bottom: position.bottom,
        left: position.left,
        width: compact ? undefined : position.width,
      }}
    >
      {isOpen && <>{children}</>}
    </ul>
  )

  return (
    <>
      {/* Placeholder at bottom of inputFrame to get correct position */}
      <div
        ref={placeholderRef}
        style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 0 }}
      />
      {/* Render menu in portal */}
      {createPortal(menuContent, portalTargetRef?.current)}
    </>
  )
})
